import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { AuthLayout } from "./Login";

export default function Register() {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(name, email, password);
      toast.success(t("auth.register_success"));
      navigate(next);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || t("auth.register_error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <h1 className="font-display text-3xl">{t("auth.register_title")}</h1>
      <p className="text-sm text-muted-foreground mt-1">{t("auth.register_sub")}</p>

      <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="register-form">
        <div className="space-y-2">
          <Label>{t("auth.register_name")}</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
            required minLength={1} data-testid="reg-name" className="h-11" />
        </div>
        <div className="space-y-2">
          <Label>{t("auth.login_email")}</Label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)}
            type="email" required data-testid="reg-email" className="h-11" />
        </div>
        <div className="space-y-2">
          <Label>{t("auth.login_password")}</Label>
          <Input value={password} onChange={(e) => setPassword(e.target.value)}
            type="password" required minLength={6} data-testid="reg-password" className="h-11" />
          <div className="text-xs text-muted-foreground">{t("auth.register_password_hint")}</div>
        </div>
        <Button type="submit" size="lg" disabled={busy}
          data-testid="reg-submit" className="w-full rounded-full h-12">
          {busy ? t("auth.register_loading") : t("auth.register_submit")}
        </Button>
      </form>

      <div className="mt-5 text-sm text-muted-foreground">
        {t("auth.have_account")}{" "}
        <Link to={`/login${next ? `?next=${encodeURIComponent(next)}` : ""}`}
          className="text-primary hover:underline" data-testid="link-login">
          {t("auth.login_link")}
        </Link>
      </div>
    </AuthLayout>
  );
}
